import { useEffect, useMemo, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import RapiDocEx from '../../components/RapiDocEx'
import RedocEx from '../../components/RedocEx'
import FunctionDocRenderer from '../../components/FunctionDocRenderer'
import { useCatalog } from '../../context/CatalogContext'
import { useTheme } from '../../context/ThemeContext'

export default function ApiDocPage() {
  const containerRef = useRef<HTMLElement | null>(null)
  const { apiId, version } = useParams()
  const { catalog, loading, error } = useCatalog()
  const { theme } = useTheme()

  const resolved = useMemo(() => {
    if (!catalog || !apiId) return null
    const api = catalog.apis.find((a) => a.id === apiId)
    if (!api) return null
    const ver = version || api.defaultVersion || api.versions[0]?.version
    const v = api.versions.find((x) => x.version === ver) || api.versions[0]
    if (!v) return null
    return { api, v }
  }, [catalog, apiId, version])

  const renderer = (resolved?.v?.renderer || 'rapidoc').toLowerCase()

  // Keep the global window scroll locked to the top only while on RapiDoc pages,
  // so navigating in RapiDoc's shadow DOM doesn't move the outer page.
  useEffect(() => {
    if (renderer !== 'rapidoc') return

    const prevRestoration = (window.history as any).scrollRestoration
    try {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual'
      }
    } catch {
    }

    const keepTop = () => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      })
    }

    // Initial lock on mount
    keepTop()

    // React to URL hash changes and history navigations
    window.addEventListener('hashchange', keepTop, { passive: true })
    window.addEventListener('popstate', keepTop)

    // Capture clicks inside the API doc page (including inside the <rapi-doc> shadow host)
    const onClickCapture = (e: Event) => {
      const t = e.target as HTMLElement | null
      if (!t) return
      const a = (t.closest && t.closest('a')) as HTMLAnchorElement | null
      if (a) {
        setTimeout(keepTop, 0)
      } else {
        setTimeout(keepTop, 0)
      }
    }

    const el = containerRef.current || document.querySelector('section.section.api-doc-page')
    if (el) el.addEventListener('click', onClickCapture, { capture: true })

    return () => {
      window.removeEventListener('hashchange', keepTop)
      window.removeEventListener('popstate', keepTop)
      if (el) el.removeEventListener('click', onClickCapture, { capture: true } as any)
      try {
        if ('scrollRestoration' in window.history) {
          ;(window.history as any).scrollRestoration = prevRestoration
        }
      } catch {
      }
    }
  }, [renderer])

  if (loading) return <p>Loading API…</p>
  if (error) {
    return (
      <section className="section api-doc-page">
        <div className="card" role="alert">Failed to load catalog: {error}</div>
      </section>
    )
  }

  if (!resolved) {
    return (
      <section className="section api-doc-page">
        <div className="card" role="alert">Unknown API or version.</div>
      </section>
    )
  }

  const { api, v } = resolved

  const specUrl = v.specUrl

  return (
    <section className="section api-spec-page" ref={(n) => (containerRef.current = n)}>
      <div className="api-spec-header">
        <div className="back-row">
          <Link to="/docs" className="back-link">← Back to docs</Link>
        </div>
        <h1 className="page-title">{api.title}</h1>
        <p className="page-subtitle">
          Version: <strong>{v.label || v.version}</strong>. Raw spec: <a href={specUrl} target="_blank" rel="noreferrer">{specUrl}</a>
        </p>
      </div>

      <div className="api-spec-body">
        {/* Include theme (and renderer) in the key to force a remount when switching
            between dark/light or viewer implementation. */}
        {renderer === 'redoc' ? (
          <RedocEx
            key={`${specUrl}::redoc::${theme}`}
            specUrl={specUrl}
            dark={theme === 'dark'}
          />
        ) : renderer === 'function-renderer' ? (
          <FunctionDocRenderer
            key={`${specUrl}::function-renderer::${theme}`}
            specUrl={specUrl}
          />
        ) : (
          <RapiDocEx
            key={`${specUrl}::rapidoc::${theme}`}
            specUrl={specUrl}
            dark={theme === 'dark'}
            // Styling-only: hide the left-nav filter on the Q-Play Rest API page
            hideSearchFilterInNav={api.id === 'qplay-rest'}
          />
        )}
      </div>
    </section>
  )
}
